/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/templates/QaTemplatesDialog.tsx
 * FILE_ID: QA-TEMPLATES-DIALOG-001
 * PURPOSE: Desktop Build-mode template library overlay, cloned from QaItpModal.
 * LANDMINE: z-40 clears the toolbar. Do not add in-flow chrome to the workspace.
 */
import { useEffect, useState } from 'react'
import { LayoutGrid, X } from 'lucide-react'
import { BoxResizeHandles, clampSize, usePersistentSize } from '@quotech/ui'
import type { JobQaTabController } from '../useJobQaTabController'
import { addArchivedStructure, type QuickTemplate } from '../../../qa/archivedStructureTemplates'
import { QaArchivedTemplateList } from './QaArchivedTemplateList'
import { QaNewTemplateForm } from './QaNewTemplateForm'
import { QaTemplateCreateMenu } from './QaTemplateCreateMenu'
import { QaTemplateAddProduct } from './QaTemplateAddProduct'
import { QaTemplateCatalogPane } from './QaTemplateCatalogPane'
import { QaTemplateContentsPane } from './QaTemplateContentsPane'
import { QaTemplateFooter } from './QaTemplateFooter'
import { QaTemplateInsertedSummary } from './QaTemplateInsertedSummary'
import { QaTemplateSystemsPane } from './QaTemplateSystemsPane'
import { useQaTemplatePicker } from './useQaTemplatePicker'

export function QaTemplatesDialog({
  open,
  onClose,
  controller,
}: {
  open: boolean
  onClose: () => void
  controller: JobQaTabController
}) {
  if (!open) return null
  return <QaTemplatesDialogBody onClose={onClose} controller={controller} />
}

function QaTemplatesDialogBody({
  onClose,
  controller,
}: {
  onClose: () => void
  controller: JobQaTabController
}) {
  const [creating, setCreating] = useState(false)
  const [archived, setArchived] = useState(false)
  const applyArchived = async (template: QuickTemplate) => {
    const inspectionId = controller.workspace.activeInspectionId
    if (!inspectionId) return
    await addArchivedStructure(controller.api.addTable.bind(controller.api), inspectionId, template)
    await controller.workspace.areasQuery.refetch()
    setArchived(false)
  }
  const picker = useQaTemplatePicker(controller.api, {
    ...controller.workspace,
    jobId: controller.props.jobId,
    pendingWriteChoice: controller.setupWriteChoice,
    consumePendingWriteChoice: controller.consumeSetupWrite,
    intoChecklist: controller.templatesInto,
  })
  const restoreDefaults = () => {
    if (!window.confirm('Remove this job\'s custom checklists from the template list? QA already added stays.')) return
    void controller.api.restoreDefaults(controller.props.jobId).then(() => picker.reload())
  }
  const [storedW, setW] = usePersistentSize('qa.templates.modal.w', 1100)
  const [storedH, setH] = usePersistentSize('qa.templates.modal.h', 720)
  const [storedX, setX] = usePersistentSize('qa.templates.modal.x', -1)
  const [storedY, setY] = usePersistentSize('qa.templates.modal.y', -1)
  const vpW = typeof window === 'undefined' ? 1200 : window.innerWidth
  const vpH = typeof window === 'undefined' ? 800 : window.innerHeight
  const bounds = { minW: 480, minH: 360, maxW: vpW - 32, maxH: vpH - 32, margin: 16, vpW, vpH }
  const w = clampSize(storedW, bounds.minW, bounds.maxW)
  const h = clampSize(storedH, bounds.minH, bounds.maxH)
  const x = clampSize(
    storedX < 0 ? (vpW - w) / 2 : storedX,
    bounds.margin,
    Math.max(bounds.margin, vpW - w - bounds.margin),
  )
  const y = clampSize(
    storedY < 0 ? (vpH - h) / 2 : storedY,
    bounds.margin,
    Math.max(bounds.margin, vpH - h - bounds.margin),
  )

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-40 bg-background/70"
      role="presentation"
      data-testid="qa-templates-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="qa-templates-dialog-title"
        data-testid="qa-templates-dialog"
        className="fixed flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-lg"
        style={{ left: x, top: y, width: w, height: h }}
      >
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-2">
          <div className="flex min-w-0 items-center gap-2">
            <LayoutGrid className="h-4 w-4 shrink-0" aria-hidden="true" />
            <div className="min-w-0">
              <h2 id="qa-templates-dialog-title" className="truncate text-sm font-semibold">Templates</h2>
              <p className="truncate text-xs text-muted-foreground">
                {controller.templatesInto
                  ? `Adding steps to: ${controller.templatesInto.name}`
                  : 'checklists go into their locations - Build mode'}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <QaTemplateCreateMenu
              phone={false}
              onNew={() => { setArchived(false); setCreating(true) }}
              onArchived={() => { setCreating(false); setArchived(true) }}
            />
            {!creating && picker.step !== 'inserted' ? (
              <QaTemplateAddProduct
                layout="popover"
                client={controller.client}
                focus={picker.productFocus}
                draft={picker.productDraft}
                locationType={picker.locationType}
                onDraft={picker.setProductDraft}
              />
            ) : null}
            <button
              type="button"
              onClick={restoreDefaults}
              className="text-xs font-medium text-primary hover:underline"
            >
              Restore defaults
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-muted-foreground hover:text-foreground"
              aria-label="Close templates"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </header>
        {archived && !creating ? (
          <QaArchivedTemplateList onPick={(template) => void applyArchived(template)} />
        ) : creating ? (
          <QaNewTemplateForm
            api={controller.api}
            jobId={controller.props.jobId}
            locationType={picker.locationType}
            onCancel={() => setCreating(false)}
            onCreated={(created) => {
              setCreating(false)
              void picker.reload().then(() => {
                picker.pickLocation(created.location_type)
                picker.pickTemplate(created.id)
              })
            }}
          />
        ) : picker.step === 'inserted' ? (
          <QaTemplateInsertedSummary
            picker={picker}
            onClose={onClose}
            onAgain={picker.reset}
          />
        ) : (
          <>
            <div className="grid min-h-0 flex-1 grid-cols-1 gap-3 p-3 md:grid-cols-[186px_268px_1fr]">
              <div className="min-h-0 overflow-hidden">
                <QaTemplateCatalogPane picker={picker} />
              </div>
              <div className="min-h-0 overflow-hidden">
                <QaTemplateSystemsPane picker={picker} />
              </div>
              <div className="flex min-h-0 flex-col overflow-hidden">
                <QaTemplateContentsPane picker={picker} />
              </div>
            </div>
            <QaTemplateFooter picker={picker} />
          </>
        )}
        <BoxResizeHandles
          label="Templates"
          box={{ x, y, w, h }}
          bounds={bounds}
          onChange={(next) => {
            setX(next.x)
            setY(next.y)
            setW(next.w)
            setH(next.h)
          }}
          axisLabels={{ e: 'Resize templates width', s: 'Resize templates height' }}
        />
      </div>
    </div>
  )
}
